import queryString from 'query-string';
import { types as sdkTypes } from './sdkLoader';
import { createResourceLocatorString } from './routes';

const { LatLng, LatLngBounds } = sdkTypes;
const DEALER_HOST_PREFIX = 'bowtackle.';
const dealerHostResolutionCache = new Map();

export const LISTING_PAGE_PENDING_APPROVAL_VARIANT = 'pending-approval';
export const LISTING_PAGE_DRAFT_VARIANT = 'draft';

export const LISTING_PAGE_PARAM_TYPE_NEW = 'new';
export const LISTING_PAGE_PARAM_TYPE_DRAFT = 'draft';
export const LISTING_PAGE_PARAM_TYPE_EDIT = 'edit';
export const LISTING_PAGE_PARAM_TYPES = [
  LISTING_PAGE_PARAM_TYPE_NEW,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
];

export const CURRENT_DOMAINS = {
  'bowtackle.a1archery.com': '66b52c46-8874-482b-8480-87449952b436',
  'bowtackle.willowcreekarchery.com': '6971635e-7019-42c8-8b46-50e125d34be3',
};

export const CURRENT_IFRAMES = {
  'bowtackle.a1archery.com': 'https://a1archery.com/a1-deals/',
  'bowtackle.willowcreekarchery.com': 'https://willowcreekarchery.com/Products/index',
};

const getNormalizedHostname = hostname =>
  typeof hostname === 'string' ? hostname.trim().toLowerCase().replace(/:\d+$/, '') : null;

export const getNormalizedWebsiteDomain = website => {
  if (!website || typeof website !== 'string') {
    return null;
  }

  const trimmed = website.trim();
  if (!trimmed) {
    return null;
  }

  const value = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const parsed = new URL(value);
    return parsed.hostname.toLowerCase().replace(/^www\./, '');
  } catch (e) {
    return trimmed
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .split('/')[0]
      .replace(/^www\./, '');
  }
};

export const getDealerHostedParentDomain = hostname => {
  const normalizedHostname = getNormalizedHostname(hostname);
  if (
    !normalizedHostname ||
    !normalizedHostname.startsWith(DEALER_HOST_PREFIX) ||
    normalizedHostname.length <= DEALER_HOST_PREFIX.length
  ) {
    return null;
  }

  return normalizedHostname.slice(DEALER_HOST_PREFIX.length);
};

export const getIframeParentPage = hostname => {
  const normalizedHostname = getNormalizedHostname(hostname);
  if (!normalizedHostname) {
    return '';
  }

  const legacyIframePage = CURRENT_IFRAMES[normalizedHostname];
  if (legacyIframePage) {
    return legacyIframePage;
  }

  const parentDomain = getDealerHostedParentDomain(normalizedHostname);
  return parentDomain ? ensureProtocol(parentDomain) : '';
};

const fetchDealerHostedStoreContext = async hostname => {
  const response = await window.fetch('/api/dealers/resolve-host', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hostname }),
  });

  if (!response.ok) {
    throw new Error(`Failed to resolve dealer host: ${response.status}`);
  }

  const data = await response.json();
  return {
    dealerId: data?.dealerId || null,
    parentDomain: data?.parentDomain || getDealerHostedParentDomain(hostname),
    matchedWebsite: data?.matchedWebsite || null,
  };
};

export const resolveDealerHostedStoreContext = async hostname => {
  const normalizedHostname = getNormalizedHostname(hostname);
  const parentDomain = getDealerHostedParentDomain(normalizedHostname);

  if (!normalizedHostname) {
    return { dealerId: null, parentDomain: null, matchedWebsite: null };
  }

  const legacyDealerId = CURRENT_DOMAINS[normalizedHostname];
  if (legacyDealerId) {
    return { dealerId: legacyDealerId, parentDomain, matchedWebsite: null };
  }

  if (!parentDomain || typeof window === 'undefined') {
    return { dealerId: null, parentDomain, matchedWebsite: null };
  }

  const cachedResolution = dealerHostResolutionCache.get(normalizedHostname);
  if (cachedResolution) {
    return cachedResolution;
  }

  const pendingResolution = fetchDealerHostedStoreContext(normalizedHostname)
    .catch(() => {
      return { dealerId: null, parentDomain, matchedWebsite: null };
    })
    .then(result => {
      dealerHostResolutionCache.set(normalizedHostname, Promise.resolve(result));
      return result;
    });

  dealerHostResolutionCache.set(normalizedHostname, pendingResolution);
  return pendingResolution;
};

// Create slug from random texts
// From Gist thread: https://gist.github.com/mathewbyrne/1280286
export const createSlug = str => {
  let text = str
    .toString()
    .toLowerCase()
    .trim();

  const sets = [
    { to: 'a', from: 'ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶ' },
    { to: 'c', from: 'ÇĆĈČ' },
    { to: 'd', from: 'ÐĎĐÞ' },
    { to: 'e', from: 'ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ' },
    { to: 'g', from: 'ĜĞĢǴ' },
    { to: 'h', from: 'ĤḦ' },
    { to: 'i', from: 'ÌÍÎÏĨĪĮİỈỊ' },
    { to: 'j', from: 'Ĵ' },
    { to: 'ij', from: 'Ĳ' },
    { to: 'k', from: 'Ķ' },
    { to: 'l', from: 'ĹĻĽŁ' },
    { to: 'm', from: 'Ḿ' },
    { to: 'n', from: 'ÑŃŅŇ' },
    { to: 'o', from: 'ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ' },
    { to: 'oe', from: 'Œ' },
    { to: 'p', from: 'ṕ' },
    { to: 'r', from: 'ŔŖŘ' },
    { to: 's', from: 'ßŚŜŞŠ' },
    { to: 't', from: 'ŢŤ' },
    { to: 'u', from: 'ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ' },
    { to: 'w', from: 'ẂŴẀẄ' },
    { to: 'x', from: 'ẍ' },
    { to: 'y', from: 'ÝŶŸỲỴỶỸ' },
    { to: 'z', from: 'ŹŻŽ' },
    { to: '-', from: "·/_,:;'" },
  ];

  sets.forEach(set => {
    text = text.replace(new RegExp(`[${set.from}]`, 'gi'), set.to);
  });

  const slug = encodeURIComponent(
    text
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/[^\w-]+/g, '') // Remove all non-word chars
      .replace(/--+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, '') // Trim - from end of text
  );

  return slug.length > 0 ? slug : 'no-slug';
};

/**
 * Parse float from a string
 *
 * @param {String} str - string to parse
 *
 * @return {Number|null} number parsed from the string, null if not a number
 */
export const parseFloatNum = str => {
  const trimmed = str && typeof str.trim === 'function' ? str.trim() : null;
  if (!trimmed) {
    return null;
  }
  const num = parseFloat(trimmed);
  const isNumber = !isNaN(num);
  const isFullyParsedNum = isNumber && num.toString() === trimmed;
  return isFullyParsedNum ? num : null;
};

/**
 * Encode a location to use in a URL
 *
 * @param {LatLng} location - location instance to encode
 *
 * @return {String} location coordinates separated by a comma
 */
export const encodeLatLng = location => `${location.lat},${location.lng}`;

/**
 * Decode a location from a string
 *
 * @param {String} str - string encoded with `encodeLatLng`
 *
 * @return {LatLng|null} location instance, null if could not parse
 */
export const decodeLatLng = str => {
  const parts = str.split(',');
  if (parts.length !== 2) {
    return null;
  }
  const lat = parseFloatNum(parts[0]);
  const lng = parseFloatNum(parts[1]);
  if (lat === null || lng === null) {
    return null;
  }
  return new LatLng(lat, lng);
};

/**
 * Encode a location bounds to use in a URL
 *
 * @param {LatLngBounds} bounds - bounds instance to encode
 *
 * @return {String} bounds coordinates separated by a comma
 */
export const encodeLatLngBounds = bounds => `${encodeLatLng(bounds.ne)},${encodeLatLng(bounds.sw)}`;

/**
 * Decode a location bounds from a string
 *
 * @param {String} str - string encoded with `encodeLatLngBounds`
 *
 * @return {LatLngBounds|null} location bounds instance, null if could not parse
 */
export const decodeLatLngBounds = str => {
  const parts = str.split(',');
  if (parts.length !== 4) {
    return null;
  }
  const ne = decodeLatLng(`${parts[0]},${parts[1]}`);
  const sw = decodeLatLng(`${parts[2]},${parts[3]}`);
  if (ne === null || sw === null) {
    return null;
  }
  return new LatLngBounds(ne, sw);
};

// Serialise SDK types in given object values into strings
const serialiseSdkTypes = obj =>
  Object.keys(obj).reduce((result, key) => {
    const val = obj[key];
    /* eslint-disable no-param-reassign */
    if (val instanceof LatLngBounds) {
      result[key] = encodeLatLngBounds(val);
    } else if (val instanceof LatLng) {
      result[key] = encodeLatLng(val);
    } else {
      result[key] = val;
    }
    /* eslint-enable no-param-reassign */
    return result;
  }, {});

/**
 * Serialise given object into a string that can be used in a
 * URL. Encode SDK types into a format that can be parsed with `parse`
 * defined below.
 *
 * @param {Object} params - object with strings/numbers/booleans or
 * SDK types as values
 *
 * @return {String} query string with sorted keys and serialised
 * values, `undefined` and `null` values are removed
 */
export const stringify = params => {
  const serialised = serialiseSdkTypes(params);
  const cleaned = Object.keys(serialised).reduce((result, key) => {
    const val = serialised[key];
    /* eslint-disable no-param-reassign */
    if (val !== null) {
      result[key] = val;
    }
    /* eslint-enable no-param-reassign */
    return result;
  }, {});
  return queryString.stringify(cleaned);
};

/**
 * Parse a URL search query. Converts numeric values into numbers,
 * 'true' and 'false' as booleans, and serialised LatLng and
 * LatLngBounds into respective instances based on given options.
 *
 * @param {String} search - query string to parse, optionally with a
 * leading '?' or '#' character
 *
 * @param {Object} options - Options for parsing:
 *
 * - latlng {Array<String} keys to parse as LatLng instances, null if
 *   not able to parse
 * - latlngBounds {Array<String} keys to parse as LatLngBounds
 *   instances, null if not able to parse
 *
 * @return {Object} key/value pairs parsed from the given String
 */
export const parse = (search, options = {}, allowEmpty = false) => {
  const { latlng = [], latlngBounds = [] } = options;
  const params = queryString.parse(search);
  return Object.keys(params).reduce((result, key) => {
    const val = params[key];
    /* eslint-disable no-param-reassign */
    if (allowEmpty && !val) {
      result[key] = true;
      return result;
    }
    if (latlng.includes(key)) {
      result[key] = decodeLatLng(val);
    } else if (latlngBounds.includes(key)) {
      result[key] = decodeLatLngBounds(val);
    } else if (val === 'true') {
      result[key] = true;
    } else if (val === 'false') {
      result[key] = false;
    } else {
      const num = parseFloatNum(val);
      result[key] = num === null ? val : num;
    }
    /* eslint-enable no-param-reassign */
    return result;
  }, {});
};

/**
 * Create Twitter page url from twitterHandle
 *
 * @param {String} twitterHandle - handle is used for generating Twitter page URL
 *
 * @return {String} twitterPageURL
 */
export const twitterPageURL = twitterHandle => {
  if (twitterHandle && twitterHandle.charAt(0) === '@') {
    return `https://twitter.com/${twitterHandle.substring(1)}`;
  } else if (twitterHandle) {
    return `https://twitter.com/${twitterHandle}`;
  }
  return null;
};

export const hasQueryParam = (location, param, value = false) => {
  let paramFound = null;
  if (location && location.search && location.search !== '') {
    paramFound = location.search
      .slice(1)
      .split('&')
      .find(p => {
        let string = p.split('=');
        if (string.length && string[0]) {
          return string[0] == param;
        }

        return false;
      });
  }

  if (paramFound && paramFound !== null && value) {
    let items = paramFound.split('=');
    if (items.length && items.length == 2 && items[1] !== '') {
      return items[1];
    }
    return null;
  }

  return paramFound && paramFound !== null;
};

export const prepareToState = (fromState, isIframe) => {
  let toState = fromState?.state ? { to: fromState } : {};
  if (isIframe) {
    if (toState.to !== undefined) {
      toState.to.search = '?iframe=true';
    } else {
      toState.to = { search: '?iframe=true' };
    }
  }

  return toState;
};

export const redirectToStore = ({
  redirect = false,
  iframe = true,
  history,
  routeConfiguration,
  id = null,
}) => {
  if (typeof window === 'undefined') {
    return Promise.resolve({ shouldRedirect: false, store: null });
  }

  let location = window.location;
  let shouldRedirect = false;
  let store = null;
  return resolveDealerHostedStoreContext(location?.hostname).then(({ dealerId }) => {
    if (dealerId !== undefined && dealerId !== null) {
      store = dealerId;
      shouldRedirect = id === null || id !== dealerId;
    }

    if (redirect && shouldRedirect) {
      let redirectPage = createResourceLocatorString(
        'StorePage',
        routeConfiguration,
        { id: store },
        { iframe }
      );
      history.push(redirectPage);
    }
    return { shouldRedirect, store };
  });
};

export const ensureProtocol = url => {
  if (!/^https?:\/\//i.test(url)) {
    return `https://${url}`;
  }
  return url;
};
